// ─────────────────────────────────────────────────────────────────────────────
// Live label previewer — the mapper screen's equivalent of the ticket studio.
//
// Draws the parsed PRN layout to scale in the printer's own dot coordinate space
// so the installer sees the sticker, not the commands: text where the printer
// will put it, real Code 128 / Code 39 bars, and the mapped loan values already
// substituted in.
//
// Values come from the backend's `resolved` map (the same numbers the print job
// will use). Tokens with no mapping yet are drawn as amber placeholders rather
// than as blanks — an empty gap looks like a finished design, a placeholder does
// not, and an unmapped barcode token is the one mistake that ruins a pouch.
// ─────────────────────────────────────────────────────────────────────────────

import React, { useId, useMemo } from "react";
import { Box, Typography } from "@mui/material";
import { QRCodeSVG } from "qrcode.react";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import { code128Bars, code39Bars } from "../paymentScreen/barcode";
import { DPMM } from "./prnParse";

const INK = "#0F172A";
const INK_MUTED = "#64748B";
const BORDER = "#E2E8F0";
const BORDER_MED = "#CBD5E1";
const AMBER = "#D97706";
const BLUE = "#2563EB";

const TOKEN_RE = /<([A-Z][A-Z0-9_]*)>/g;

// Substitute the tokens in one element's data.
//
// The printer puts *nothing* where an unmapped token was, so that is the default:
// the preview shows the tag as it will come out. Standing the token's name in its
// place is an inspection aid — useful for answering "if I map this, where does it
// land?" — so it is opt-in via `showUnmapped`. Either way the token is reported
// back so the warning list can name it.
const fillTokens = (raw, resolved, mapped, showUnmapped) => {
  let missing = null;
  const text = String(raw == null ? "" : raw).replace(TOKEN_RE, (_m, tok) => {
    if (mapped?.has(tok)) return resolved?.[tok] ?? "";
    missing = missing || tok;
    return showUnmapped ? tok : "";
  });
  return { text, missing };
};

// Advance width per character.
//
// Bitmap faces (ZPL A–Z, TSPL 1–8) are fixed-pitch, so the cell width the design
// declares is exact. The scalable faces (ZPL font 0, TSPL font 0) are
// proportional and their width parameter is a *maximum* cell size, not a stretch
// factor — designs routinely write ^A0N,26,26, which is the normal square
// default and not a 1.7× wide font. Honouring it would smear every heading, so
// it is ignored in favour of an average advance. Estimate either way, which is
// all this is used for: spotting a field that runs off the edge of the tag.
const charWidth = (el) => (el.mono && el.w > 0 ? el.w : el.h * 0.52);

// A rotated element's extent swaps axes; only the far edge matters here. `top`
// is the element's real upper edge, which for ^FT text sits a cap-height *above*
// the anchor — treating the anchor as the top would report every bottom-row
// field as clipped.
const extent = (el, w, h) => {
  const top = el.kind === "text" && el.baseline ? el.y - h * 0.82 : el.y;
  const r = (((el.rot || 0) % 360) + 360) % 360;
  if (r === 90 || r === 270) return { right: el.x + h, bottom: top + w };
  return { right: el.x + w, bottom: top + h };
};

// QR module count by payload size (byte mode, level M). qrcode.react picks the
// real version internally; this only sizes the box it is drawn into, so the
// printed cell size (the ZPL magnification) stays honest.
const qrModules = (data) => {
  const l = String(data || "").length;
  if (l <= 14) return 21;
  if (l <= 26) return 25;
  if (l <= 42) return 29;
  if (l <= 62) return 33;
  if (l <= 84) return 37;
  if (l <= 106) return 41;
  return 45;
};

// ─── Tag silhouette ─────────────────────────────────────────────────────────
//
// A jewellery tag is a flag on a strap, not a rectangle: a rounded rectangular
// body that carries the whole print, and one narrow tail of constant height,
// centred on the body and capped with a semicircle, that is wrapped around the
// ring shank or chain and stuck back onto itself.
//
// The printer covers the entire strip, but only the body can be read once the tag
// is fitted — so where the body ends matters more to the installer than the edge
// of the media does. The body meets the tail as a square step, which is how the
// die cut looks; there is no second lobe at the far end.
const tagPath = ({ W, H, L1, th }) => {
  const r = Math.max(1, Math.min(H * 0.16, L1 / 2));
  const tail = Math.min(th, H);
  const ty = (H - tail) / 2;
  // A fully rounded cap: the arc's endpoints are one tail-height apart, so a
  // radius of half that traces an exact semicircle out to x = W.
  const cr = Math.max(0.5, Math.min(tail / 2, W - L1));
  return [
    `M ${r} 0`,
    `L ${L1 - r} 0`,
    `A ${r} ${r} 0 0 1 ${L1} ${r}`,
    `L ${L1} ${ty}`,
    `L ${W - cr} ${ty}`,
    `A ${cr} ${cr} 0 0 1 ${W - cr} ${ty + tail}`,
    `L ${L1} ${ty + tail}`,
    `L ${L1} ${H - r}`,
    `A ${r} ${r} 0 0 1 ${L1 - r} ${H}`,
    `L ${r} ${H}`,
    `A ${r} ${r} 0 0 1 0 ${H - r}`,
    `L 0 ${r}`,
    `A ${r} ${r} 0 0 1 ${r} 0`,
    "Z",
  ].join(" ");
};

const roundedPath = (W, H) => {
  const r = Math.min(H * 0.16, 10);
  return [
    `M ${r} 0`,
    `L ${W - r} 0`,
    `A ${r} ${r} 0 0 1 ${W} ${r}`,
    `L ${W} ${H - r}`,
    `A ${r} ${r} 0 0 1 ${W - r} ${H}`,
    `L ${r} ${H}`,
    `A ${r} ${r} 0 0 1 0 ${H - r}`,
    `L 0 ${r}`,
    `A ${r} ${r} 0 0 1 ${r} 0`,
    "Z",
  ].join(" ");
};

const LabelPreview = ({
  parsed,
  resolved,
  mappedTokens,
  widthMm = 92,
  heightMm = 15,
  zoom = 1,
  shape = "tag",
  printAreaMm = 52,
  tailHeightMm = 3.5,
  showUnmapped = false,
}) => {
  // Unique per instance: two previewers on one page must not share a clip path.
  // useId returns colon-wrapped values, which are legal HTML ids but awkward
  // inside url(#…) references, so they are stripped.
  const uid = useId().replace(/[^a-zA-Z0-9]/g, "");
  const clipId = `clip${uid}`;
  const maskId = `mask${uid}`;
  const contentId = `body${uid}`;
  const W = Math.max(8, Math.round(widthMm * DPMM));
  const H = Math.max(8, Math.round(heightMm * DPMM));

  // Where the body ends: everything to the right of it is strap, and disappears
  // once the tag is wrapped around the ornament.
  const isTag = shape === "tag" && printAreaMm > 2 && printAreaMm < widthMm - 2;
  const L1 = isTag ? Math.round(printAreaMm * DPMM) : W;
  const outline = isTag
    ? tagPath({ W, H, L1, th: Math.max(2, tailHeightMm * DPMM) })
    : roundedPath(W, H);

  // Resolve every element once, and collect what the installer needs to know
  // about the result in the same pass.
  const { drawn, unmapped, overflow, pastFold } = useMemo(() => {
    const out = [];
    const missing = new Set();
    let over = false;
    let fold = false;

    // Two different problems, two different fixes: past the media edge the
    // printer clips it, past the fold it prints perfectly and then vanishes into
    // the wrap. The second is the one that gets missed on a bench test.
    const mark = (el, w, h) => {
      const e = extent(el, w, h);
      if (e.right > W + 2 || e.bottom > H + 2) over = true;
      else if (isTag && e.right > L1 + 2) fold = true;
    };

    for (const el of parsed?.elements || []) {
      const { text, missing: miss } = fillTokens(
        el.raw,
        resolved,
        mappedTokens,
        showUnmapped,
      );
      if (miss) missing.add(miss);

      // A field left with nothing to print is not drawn at all — and, just as
      // importantly, is not measured, so an unmapped field can never raise a
      // clipping or strap warning about ink that is never laid down.
      if (el.kind !== "box" && !text && !showUnmapped) continue;

      let w = 0;
      let h = 0;
      if (el.kind === "text") {
        w = charWidth(el) * text.length;
        h = el.h;
      } else if (el.kind === "barcode") {
        // An unmapped barcode token is *not* encoded: bars spelling out the
        // token's own name would scan cleanly and look finished, which is the
        // single worst thing this preview could imply.
        const bars = miss
          ? { bars: [], width: 0 }
          : el.symbology === "code39"
            ? code39Bars(text, el.ratio)
            : code128Bars(text);
        w = bars.width * el.module;
        h = el.height + (el.human ? Math.max(12, el.height * 0.22) : 0);
        out.push({ ...el, text, missing: miss, bars, w, h });
        mark(el, w, h);
        continue;
      } else if (el.kind === "qr") {
        w = h = qrModules(text) * el.mag;
        // ^FT gave the bottom-left corner; now that the payload has fixed the
        // symbol size, turn that back into a top-left origin.
        if (el.baseline) {
          out.push({ ...el, y: el.y - h, text, missing: miss, w, h });
          mark({ ...el, y: el.y - h }, w, h);
          continue;
        }
      } else {
        w = el.w;
        h = el.h;
      }

      out.push({ ...el, text, missing: miss, w, h });
      mark(el, w, h);
    }

    return {
      drawn: out,
      unmapped: [...missing],
      overflow: over,
      pastFold: fold,
    };
  }, [parsed, resolved, mappedTokens, W, H, isTag, L1, showUnmapped]);

  const svgWidth = `${Math.round(zoom * 100)}%`;

  // ── Empty state ───────────────────────────────────────────────────────────
  if (!parsed?.elements?.length) {
    return (
      <Box
        sx={{
          flex: 1,
          minHeight: 140,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
          gap: 0.5,
          color: INK_MUTED,
          px: 1,
        }}
      >
        <Box
          sx={{
            width: 92,
            height: 16,
            borderRadius: 0.75,
            border: `1px dashed ${BORDER_MED}`,
            mb: 0.75,
          }}
        />
        <Typography sx={{ fontSize: 12.5, fontWeight: 700 }}>
          Nothing to preview yet
        </Typography>
        <Typography sx={{ fontSize: 11.5, maxWidth: 260 }}>
          {parsed?.notes?.[0] ||
            "Upload or paste the PRN template — the sticker as the printer will lay it out appears here."}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        gap: 0.75,
      }}
    >
      {/* Scroll container: at 1× the label fits the panel, above that it is
          scrolled horizontally rather than shrunk. */}
      <Box
        sx={{
          overflowX: "auto",
          overflowY: "hidden",
          pb: 0.5,
          scrollbarWidth: "thin",
          "&::-webkit-scrollbar": { height: 6 },
          "&::-webkit-scrollbar-thumb": {
            background: BORDER_MED,
            borderRadius: 3,
          },
        }}
      >
        <Box sx={{ width: svgWidth, minWidth: svgWidth }}>
          {/* mm ruler. Positioned in CSS percentages rather than drawn in the
              SVG: the label is scaled by width alone, so anything inside its
              coordinate space would be stretched horizontally as the zoom rises
              — fine for bars, unreadable for tick numbers. */}
          <Box sx={{ position: "relative", height: 13 }}>
            {Array.from({ length: Math.floor(widthMm / 10) + 1 }, (_, i) => (
              <Box
                key={i}
                sx={{
                  position: "absolute",
                  left: `${((i * 10) / widthMm) * 100}%`,
                  bottom: 0,
                  height: 5,
                  borderLeft: `1px solid ${BORDER_MED}`,
                }}
              >
                {i > 0 && (
                  <Typography
                    sx={{
                      position: "absolute",
                      left: 2,
                      bottom: 4,
                      fontSize: 8.5,
                      lineHeight: 1,
                      color: INK_MUTED,
                    }}
                  >
                    {i * 10}
                  </Typography>
                )}
              </Box>
            ))}
          </Box>

          {/* The tag itself, cut to its real outline. The die cut is drawn in the
              SVG rather than with CSS borders because a barbell tag is not a
              rectangle, and the notches either side of the neck are liner, not
              label — content there must not appear at all. */}
          <Box
            component="svg"
            viewBox={`0 0 ${W} ${H}`}
            sx={{
              width: "100%",
              height: "auto",
              display: "block",
              filter: "drop-shadow(0 1px 2px rgba(15,23,42,0.18))",
              overflow: "visible",
            }}
          >
            {/* Tag stock */}
            <path
              d={outline}
              fill="#fff"
              stroke={BORDER_MED}
              strokeWidth={1.5}
            />

            {/* The design is drawn twice from one definition: solid where it
                lands on the tag, and as a faint ghost where it does not. The
                printer really does put ink on the liner in the notches, and a
                field that simply disappeared would leave the installer hunting
                for a value that is in fact being printed into the bin. */}
            <use href={`#${contentId}`} clipPath={`url(#${clipId})`} />
            <use
              href={`#${contentId}`}
              mask={`url(#${maskId})`}
              opacity={0.17}
            />

            <defs>
              <clipPath id={clipId}>
                <path d={outline} />
              </clipPath>
              {/* White shows, black hides: the media rectangle minus the die
                  cut, i.e. everything that misses the tag. */}
              <mask id={maskId}>
                <rect x={0} y={0} width={W} height={H} fill="#fff" />
                <path d={outline} fill="#000" />
              </mask>

              <g id={contentId} shapeRendering="crispEdges">
              {/* 1 mm safe margin, inside the printable lobe — designs that
                  touch this edge tend to clip */}
              <rect
                x={DPMM}
                y={DPMM}
                width={Math.max(0, (isTag ? L1 : W) - 2 * DPMM)}
                height={Math.max(0, H - 2 * DPMM)}
                fill="none"
                stroke={BLUE}
                strokeOpacity={0.16}
                strokeDasharray="6 6"
                strokeWidth={1}
              />

              {/* No separate fold marker is needed: with the die cut drawn, the
                  step where the body becomes strap is the boundary. */}
              {drawn.map((el) => {
                const rot = el.rot
                  ? `rotate(${el.rot} ${el.x} ${el.y})`
                  : undefined;

                if (el.kind === "box")
                  return (
                    <rect
                      key={el.id}
                      transform={rot}
                      x={el.x}
                      y={el.y}
                      width={Math.max(0, el.w)}
                      height={Math.max(0, el.h)}
                      fill={el.filled ? "#000" : "none"}
                      stroke={el.filled ? "none" : "#000"}
                      strokeWidth={el.thickness || 1}
                    />
                  );

                if (el.kind === "qr") {
                  const size = el.w;
                  if (!el.text)
                    return (
                      <rect
                        key={el.id}
                        transform={rot}
                        x={el.x}
                        y={el.y}
                        width={size}
                        height={size}
                        fill="none"
                        stroke={AMBER}
                        strokeDasharray="4 4"
                        strokeWidth={2}
                      />
                    );
                  return (
                    <g
                      key={el.id}
                      transform={`translate(${el.x} ${el.y})${
                        el.rot ? ` rotate(${el.rot})` : ""
                      }`}
                    >
                      <QRCodeSVG
                        value={el.text}
                        size={size}
                        level="M"
                        marginSize={0}
                        bgColor="#FFFFFF"
                        fgColor="#000000"
                      />
                    </g>
                  );
                }

                if (el.kind === "barcode") {
                  const lineH = Math.max(12, el.height * 0.22);
                  if (!el.bars.width)
                    return (
                      <g key={el.id} transform={rot}>
                        <rect
                          x={el.x}
                          y={el.y}
                          width={Math.min(180, Math.max(60, W - el.x - DPMM))}
                          height={el.height}
                          fill="none"
                          stroke={AMBER}
                          strokeDasharray="5 5"
                          strokeWidth={2}
                        />
                        <text
                          x={el.x + 6}
                          y={el.y + el.height / 2 + 5}
                          fontSize={14}
                          fill={AMBER}
                          fontFamily="Arial, sans-serif"
                          fontWeight="bold"
                        >
                          {el.missing || "no barcode data"}
                        </text>
                      </g>
                    );
                  return (
                    <g key={el.id} transform={rot}>
                      {el.bars.bars.map((b, i) => (
                        <rect
                          key={i}
                          x={el.x + b.x * el.module}
                          y={el.y + (el.above ? lineH : 0)}
                          width={b.w * el.module}
                          height={el.height}
                          fill="#000"
                        />
                      ))}
                      {el.human && (
                        <text
                          x={el.x + (el.bars.width * el.module) / 2}
                          y={
                            el.above
                              ? el.y + lineH - 2
                              : el.y + el.height + lineH - 2
                          }
                          fontSize={lineH}
                          fill="#000"
                          textAnchor="middle"
                          fontFamily="Arial, sans-serif"
                        >
                          {el.text}
                        </text>
                      )}
                    </g>
                  );
                }

                // Text. ^FO places the top of the character cell, ^FT its
                // baseline — getting that wrong shifts every field by a line.
                const y = el.baseline ? el.y : el.y + el.h * 0.82;
                // SVG monospace advances at roughly 0.6 em. Squeeze it onto the
                // printer's declared cell so a fixed-pitch design lines up
                // column for column; proportional faces are left alone.
                const sx =
                  el.mono && el.w > 0 && el.h > 0
                    ? Math.max(0.4, Math.min(2, el.w / (el.h * 0.6)))
                    : 1;
                const scale =
                  sx !== 1
                    ? ` translate(${el.x} 0) scale(${sx.toFixed(
                        3,
                      )} 1) translate(${-el.x} 0)`
                    : "";
                // Amber as soon as *any* token in the field is unmapped, even
                // when the rest of the line resolved — "F1 DATE" rendered in
                // black reads as a finished field that just says DATE. Only
                // reachable while the toggle is on; with it off the field shows
                // just its mapped part, which is genuinely what prints.
                const isPlaceholder = showUnmapped && !!el.missing;

                return (
                  <g key={el.id} transform={`${rot || ""}${scale}`}>
                    {el.invert && el.text && (
                      <rect
                        x={el.x - 2}
                        y={el.y}
                        width={charWidth(el) * el.text.length + 4}
                        height={el.h}
                        fill="#000"
                      />
                    )}
                    <text
                      x={el.x}
                      y={y}
                      fontSize={el.h}
                      fill={
                        isPlaceholder ? AMBER : el.invert ? "#FFF" : "#000"
                      }
                      fontFamily={
                        el.mono
                          ? "Consolas, 'Courier New', monospace"
                          : "Arial, Helvetica, sans-serif"
                      }
                      fontStyle={isPlaceholder ? "italic" : "normal"}
                      style={{ whiteSpace: "pre" }}
                    >
                      {el.text || ""}
                    </text>
                    {isPlaceholder && (
                      <line
                        x1={el.x}
                        y1={el.y + el.h}
                        x2={el.x + charWidth(el) * el.text.length}
                        y2={el.y + el.h}
                        stroke={AMBER}
                        strokeWidth={1.5}
                        strokeDasharray="3 3"
                      />
                    )}
                  </g>
                );
              })}
              </g>
            </defs>
          </Box>
        </Box>
      </Box>

      {/* ── Caption & warnings ── */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
        <Typography sx={{ fontSize: 10.5, color: INK_MUTED, fontWeight: 700 }}>
          {widthMm} × {heightMm} mm
          {isTag ? ` · ${printAreaMm} mm readable` : ""} · 203 dpi ·{" "}
          {parsed.language}
        </Typography>
        <Typography sx={{ fontSize: 10.5, color: INK_MUTED, ml: "auto" }}>
          {drawn.length} element{drawn.length === 1 ? "" : "s"}
        </Typography>
      </Box>

      {(unmapped.length > 0 ||
        overflow ||
        pastFold ||
        parsed.notes?.length > 0) && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 0.4,
            border: `1px solid ${BORDER}`,
            borderRadius: 1.5,
            bgcolor: "#FFFBEB",
            p: 0.85,
          }}
        >
          {[
            unmapped.length
              ? (showUnmapped
                  ? "Still unmapped, shown in amber where they would land: "
                  : "Not mapped, so nothing prints for them — tick “Show unmapped” to see where they would land: ") +
                unmapped.map((t) => `<${t}>`).join(", ")
              : null,
            overflow
              ? "Some fields fall outside the label — they will be clipped by the printer."
              : null,
            pastFold
              ? `Some fields sit past the ${printAreaMm} mm body. They print fine, but that part of the tag is the strap that wraps around the ornament, so nobody will be able to read or scan them.`
              : null,
            ...(parsed.notes || []),
          ]
            .filter(Boolean)
            .map((msg, i) => (
              <Box key={i} sx={{ display: "flex", gap: 0.6 }}>
                <WarningAmberRoundedIcon
                  sx={{ fontSize: 13, color: AMBER, mt: "1px", flexShrink: 0 }}
                />
                <Typography sx={{ fontSize: 10.8, color: INK, lineHeight: 1.4 }}>
                  {msg}
                </Typography>
              </Box>
            ))}
        </Box>
      )}
    </Box>
  );
};

export default LabelPreview;
